import ExcelJS from 'exceljs';
import {
  QUESTIONS,
  QUESTION_BY_KEY,
  SECTIONS,
  TEMPLATE_LEDGER,
  type TemplateLedger,
  normalizeAccountOverlays,
  normalizeJournalOverlays,
  type AccountOverlay,
  type Answers,
  type JournalOverlay,
  type Option,
  type Question,
  type QuestionContext,
  type TemplateConfig,
} from '@lg/shared';
import {
  addPeopleSheets,
  cellText,
  loadWorkbook,
  readPeopleSheets,
  type UploadedRow,
} from '../people/excel.js';
import type { SampleRow } from '../people/samples.js';

/**
 * Request workbook: one .xlsx that carries a whole new venue request. The
 * "Request" sheet holds every intake answer as a Key / Answer row; the people
 * and approvals sheets follow, in the same format as the people template.
 */

export const REQUEST_SHEET = 'Request';
const ADDRESS_PARTS = ['street', 'city', 'state', 'zip'] as const;
const HEADER = ['Section', 'Key', 'Question', 'Answer', 'Allowed values / format', 'Required'];

/**
 * The ledger overlays have a sheet each (one row per overlay) instead of a Request row. Blank
 * sheets mean the template as is.
 */
export const JOURNAL_OVERLAY_SHEET = 'Journal overlays';
export const ACCOUNT_OVERLAY_SHEET = 'Account overlays';
const JOURNAL_HEADER = ['Journal name', 'Description', 'From main account', 'To main account'];
const accountHeader = (ledger: TemplateLedger): string[] => [
  'Main account',
  'Suspend for this venue (yes / no)',
  `Default ${ledger.defaultDimension.name}`,
];
const OVERLAY_TYPES: ReadonlySet<Question['type']> = new Set(['journals', 'accountOverrides']);

/** When a conditional question is required, in words. */
const REQUIRED_WHEN: Record<string, string> = {
  'finance.deviationReason': 'If currency, fiscal calendar or chart of accounts differ from the template',
  'finance.resetToSmallest': 'If copying number sequences',
  'procurement.vendorIds': 'If vendors to copy is Selected',
  'ledger.overlayReason': `If the ${JOURNAL_OVERLAY_SHEET} or ${ACCOUNT_OVERLAY_SHEET} sheet has rows`,
};

interface SheetRow {
  key: string;
  question: Question;
  label: string;
  allowed: string;
  dropdown: string[] | null;
}

function sheetRows(ctx: QuestionContext): SheetRow[] {
  const out: SheetRow[] = [];
  for (const q of QUESTIONS) {
    if (OVERLAY_TYPES.has(q.type)) continue; // own sheets
    if (q.type === 'address') {
      for (const part of ADDRESS_PARTS)
        out.push({
          key: `${q.key}.${part}`,
          question: q,
          label: `${q.label}: ${part === 'zip' ? 'ZIP' : part}`,
          allowed: part === 'state' ? '2-letter state code' : '',
          dropdown: null,
        });
      continue;
    }
    const options = q.options?.(ctx) ?? [];
    let allowed = '';
    let dropdown: string[] | null = null;
    switch (q.type) {
      case 'date':
        allowed = 'yyyy-mm-dd';
        break;
      case 'yesno':
        allowed = 'yes / no';
        dropdown = ['yes', 'no'];
        break;
      case 'choice':
        allowed = options.map(describe).join('; ');
        dropdown = options.map((o) => o.value);
        break;
      case 'multi':
      case 'workflows':
        allowed = `Comma-separated: ${options.map(describe).join('; ')}`;
        break;
      case 'vendors':
        allowed = 'Comma-separated vendor account numbers from the template company';
        break;
      default:
        if (q.sensitive) allowed = 'Format 12-3456789; stored masked';
    }
    out.push({ key: q.key, question: q, label: q.label, allowed, dropdown });
  }
  return out;
}

const describe = (o: Option): string => (o.label === o.value ? o.value : `${o.value} (${o.label})`);

function display(q: Question, value: unknown): string | Date {
  if (value === undefined || value === null) return '';
  if (q.type === 'yesno') return value === true ? 'yes' : value === false ? 'no' : '';
  if (Array.isArray(value)) return value.map(String).join(', ');
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** Builds the request workbook. Blank when `answers` is empty; an example otherwise. */
export async function buildRequestWorkbook(input: {
  template: TemplateConfig;
  ctx: QuestionContext;
  answers?: Answers;
  peopleRows?: readonly SampleRow[];
}): Promise<Uint8Array> {
  const answers = input.answers ?? {};
  const ledger = input.ctx.ledger ?? TEMPLATE_LEDGER;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Legends venue onboarding';

  const ws = wb.addWorksheet(REQUEST_SHEET, { views: [{ state: 'frozen', ySplit: 4 }] });
  ws.columns = [{ width: 22 }, { width: 30 }, { width: 46 }, { width: 40 }, { width: 70 }, { width: 22 }];
  // Answers are text: keeps ZIP leading zeros and stops Excel turning dates into serials.
  ws.getColumn(4).numFmt = '@';
  ws.addRow(['New venue request']).font = { bold: true, size: 14 };
  ws.addRow([
    'Fill the Answer column, then the people sheets, then (optional) the customers, vendors, projectContracts and projects sheets. ' +
      `Venue-only ledger changes go on the ${JOURNAL_OVERLAY_SHEET} and ${ACCOUNT_OVERLAY_SHEET} sheets. ` +
      'Upload this file on New request to create the request in one step. ' +
      'Blank answers take the template default. Keep the Key column as it is.',
  ]);
  ws.addRow([]);
  const header = ws.addRow(HEADER);
  header.font = { bold: true };

  const sectionTitle = new Map<string, string>(SECTIONS.map((s) => [s.id, s.title]));
  for (const r of sheetRows(input.ctx)) {
    const value =
      r.question.type === 'address'
        ? (answers[r.question.key] as Record<string, unknown> | undefined)?.[r.key.split('.').at(-1) ?? '']
        : answers[r.key];
    const required =
      typeof r.question.required === 'boolean'
        ? r.question.required
          ? 'Yes'
          : 'No'
        : (REQUIRED_WHEN[r.key] ?? 'Sometimes');
    const row = ws.addRow([
      sectionTitle.get(r.question.section) ?? r.question.section,
      r.key,
      r.label,
      display(r.question, value),
      r.allowed,
      required,
    ]);
    row.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8DC' } };
    if (r.dropdown) {
      row.getCell(4).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${r.dropdown.join(',')}"`],
        showErrorMessage: true,
        errorTitle: 'Not an allowed value',
        error: `Pick one of: ${r.dropdown.join(', ')}`,
      };
    }
  }

  addOverlaySheet(
    wb,
    JOURNAL_OVERLAY_SHEET,
    'Overlay B: new journal names with journal control',
    `One row per journal name the venue needs that the template does not have (template journals: ${ledger.journalNames.join(', ')}). ` +
      'Each journal can then only post to main accounts in its range. Name: 1–10 letters or digits. Leave blank for none.',
    JOURNAL_HEADER,
    normalizeJournalOverlays(answers['ledger.journals']).map((r: JournalOverlay) => [
      r.name,
      r.description,
      r.fromAccount,
      r.toAccount,
    ]),
  );
  addOverlaySheet(
    wb,
    ACCOUNT_OVERLAY_SHEET,
    'Overlay C: main account overrides for this venue only',
    'One row per main account to suspend or give a default dimension in this legal entity. The chart of accounts is shared: ' +
      'the template company and other venues keep the account as it is. Main accounts are 6 digits. Leave blank for none.',
    accountHeader(ledger),
    normalizeAccountOverlays(answers['ledger.accountOverrides']).map((r: AccountOverlay) => [
      r.mainAccountId,
      r.suspend ? 'yes' : 'no',
      r.defaultDimensionValue,
    ]),
  );
  addPeopleSheets(wb, input.template, input.peopleRows ?? []);
  const buf = await wb.xlsx.writeBuffer();
  return new Uint8Array(buf as ArrayBuffer);
}

/** An overlay sheet: title, instructions, header on row 4, then one row per overlay. */
function addOverlaySheet(
  wb: ExcelJS.Workbook,
  name: string,
  title: string,
  instructions: string,
  header: readonly string[],
  rows: string[][],
): void {
  const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 4 }] });
  ws.columns = header.map((_, i) => ({ width: i === 1 ? 40 : 24 }));
  // Text cells: main accounts keep their digits as typed.
  header.forEach((_, i) => (ws.getColumn(i + 1).numFmt = '@'));
  ws.addRow([title]).font = { bold: true, size: 14 };
  ws.addRow([instructions]);
  ws.addRow([]);
  ws.addRow([...header]).font = { bold: true };
  for (const r of rows) ws.addRow(r);
}

/** Rows under the overlay sheet's header (found by its first column), as text. */
function readOverlaySheet(wb: ExcelJS.Workbook, name: string, firstHeader: string): string[][] | null {
  const ws = wb.getWorksheet(name);
  if (!ws) return null;
  const table: string[][] = [];
  ws.eachRow({ includeEmpty: false }, (row) => {
    const values = Array.isArray(row.values) ? row.values.slice(1) : [];
    table.push(values.map((v) => String(cellText(v as ExcelJS.CellValue) ?? '').trim()));
  });
  const at = table.findIndex((r) => r[0]?.toLowerCase() === firstHeader.toLowerCase());
  return at < 0 ? [] : table.slice(at + 1).filter((r) => r.some(Boolean));
}

const yes = (s: string): boolean | null => {
  const v = s.trim().toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(v)) return true;
  if (['', 'no', 'n', 'false', '0'].includes(v)) return false;
  return null;
};

function readOverlaySheets(wb: ExcelJS.Workbook, answers: Answers, messages: string[]): void {
  const journals = readOverlaySheet(wb, JOURNAL_OVERLAY_SHEET, JOURNAL_HEADER[0] ?? '');
  if (journals?.length)
    answers['ledger.journals'] = journals.map(([name = '', description = '', from = '', to = '']) => ({
      name,
      description,
      fromAccount: from,
      toAccount: to,
    }));
  const accounts = readOverlaySheet(wb, ACCOUNT_OVERLAY_SHEET, 'Main account');
  if (accounts?.length)
    answers['ledger.accountOverrides'] = accounts.map(([id = '', suspend = '', dim = ''], i) => {
      const s = yes(suspend);
      if (s === null) messages.push(`${ACCOUNT_OVERLAY_SHEET} account ${i + 1}: suspend must be yes or no`);
      return { mainAccountId: id, suspend: s === true, defaultDimensionValue: dim.toUpperCase() };
    });
}

export interface RequestWorkbookResult {
  /** Present answers only; blanks are left out so template defaults apply. */
  answers: Answers;
  peopleRows: UploadedRow[];
  /** Problems with individual rows; the rest of the file still loads. */
  messages: string[];
  /** Set when the file cannot be used at all. */
  fatal: string | null;
}

function matchOption(raw: string, options: readonly Option[]): string | null {
  const s = raw.trim().toLowerCase();
  const hit = options.find(
    (o) => o.value.toLowerCase() === s || o.label.toLowerCase() === s || describe(o).toLowerCase() === s,
  );
  return hit?.value ?? null;
}

const splitList = (s: string): string[] =>
  s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);

export async function parseRequestWorkbook(
  bytes: Uint8Array,
  ctx: QuestionContext,
): Promise<RequestWorkbookResult> {
  const empty = { answers: {}, peopleRows: [], messages: [] };
  const wb = await loadWorkbook(bytes);
  if (typeof wb === 'string') return { ...empty, fatal: wb };
  const ws = wb.getWorksheet(REQUEST_SHEET);
  if (!ws)
    return { ...empty, fatal: `No "${REQUEST_SHEET}" sheet; download the request template from New request` };

  const table: unknown[][] = [];
  ws.eachRow({ includeEmpty: false }, (row, n) => {
    const values = Array.isArray(row.values) ? row.values.slice(1) : [];
    table[n - 1] = values.map((v) => cellText(v as ExcelJS.CellValue));
  });
  const headerIndex = table.findIndex((r) => r?.some((c) => String(c).trim().toLowerCase() === 'key'));
  const header = table[headerIndex];
  if (!header) return { ...empty, fatal: `The "${REQUEST_SHEET}" sheet has no Key / Answer header row` };
  const keyCol = header.findIndex((c) => String(c).trim().toLowerCase() === 'key');
  const answerCol = header.findIndex((c) => String(c).trim().toLowerCase() === 'answer');
  if (answerCol < 0) return { ...empty, fatal: `The "${REQUEST_SHEET}" sheet has no Answer column` };

  const answers: Answers = {};
  const messages: string[] = [];
  const address: Record<string, string> = {};
  table.slice(headerIndex + 1).forEach((cells, i) => {
    if (!cells) return;
    const rowNumber = headerIndex + i + 2;
    const key = String(cells[keyCol] ?? '').trim();
    const raw = cells[answerCol];
    const text = raw === undefined || raw === null ? '' : String(raw).trim();
    if (!key || !text) return;

    const addressPart = ADDRESS_PARTS.find((p) => key === `entity.address.${p}`);
    if (addressPart) {
      address[addressPart] = addressPart === 'state' ? text.toUpperCase() : text;
      return;
    }
    const q = QUESTION_BY_KEY.get(key);
    if (!q) {
      messages.push(`${REQUEST_SHEET} row ${rowNumber}: unknown key "${key}" ignored`);
      return;
    }
    const options = q.options?.(ctx) ?? [];
    if (OVERLAY_TYPES.has(q.type)) {
      messages.push(
        `${REQUEST_SHEET} row ${rowNumber}: ${q.label} goes on the ${q.type === 'journals' ? JOURNAL_OVERLAY_SHEET : ACCOUNT_OVERLAY_SHEET} sheet; ignored here`,
      );
      return;
    }
    switch (q.type) {
      case 'yesno': {
        const v = text.toLowerCase();
        if (['yes', 'y', 'true', '1'].includes(v)) answers[key] = true;
        else if (['no', 'n', 'false', '0'].includes(v)) answers[key] = false;
        else messages.push(`${REQUEST_SHEET} row ${rowNumber}: ${q.label} must be yes or no`);
        break;
      }
      case 'choice': {
        const v = matchOption(text, options);
        if (v) answers[key] = v;
        else messages.push(`${REQUEST_SHEET} row ${rowNumber}: "${text}" is not an option for ${q.label}`);
        break;
      }
      case 'multi':
      case 'workflows': {
        const values: string[] = [];
        for (const item of splitList(text)) {
          const v = matchOption(item, options);
          if (v) values.push(v);
          else messages.push(`${REQUEST_SHEET} row ${rowNumber}: "${item}" is not an option for ${q.label}`);
        }
        if (values.length) answers[key] = values;
        break;
      }
      case 'vendors':
        answers[key] = splitList(text).map((v) => v.toUpperCase());
        break;
      default:
        answers[key] = text;
    }
  });
  if (Object.keys(address).length)
    answers['entity.address'] = { street: '', city: '', state: '', zip: '', ...address };
  readOverlaySheets(wb, answers, messages);

  const people = readPeopleSheets(wb);
  return { answers, peopleRows: people.rows, messages: [...messages, ...people.fileErrors], fatal: null };
}
